import { css } from '@emotion/react'
import axios from 'axios'
import { Card, Cell, Grid, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { subMinutes } from 'date-fns'
import { RefObject, useCallback, useEffect, useState } from 'react'

import { HeaderPainelChamada } from './components/HeaderPainelChamada'
import { PainelChamadaDestaque } from './components/PainelChamadaDestaque'
import { PainelChamadasErrorView } from './components/PainelChamadasErrorView'
import { CHAMADA_PAINEL_TIMERS } from './constants-chamadaPainel'
import { useChamadaAudio } from './hooks/useChamadaAudio'
import { useFilaChamadas } from './hooks/useFilaChamadas'
import { usePainelChamadasSession } from './hooks/usePainelChamadasSession'
import { PainelChamadasListener } from './PainelChamadasListener'

interface PainelChamadasViewProps {
  tokenAcesso: string
  uuidDispositivo: string
  audioRef: RefObject<HTMLAudioElement>
}

export function PainelChamadasView(props: Readonly<PainelChamadasViewProps>) {
  const { tokenAcesso, uuidDispositivo, audioRef } = props
  const theme = useTheme()

  const [onMessageError, setOnMessageError] = useState(false)

  const { isAuthenticated, hasError, nomeUnidadeSaude, chamadasIniciais, atualizarChamadas } = usePainelChamadasSession(
    {
      tokenAcesso,
      uuidDispositivo,
    }
  )

  const { chamadaAtual, chamadasProcessadas, isChamando, setEstadoInicial, enfileirar } = useFilaChamadas(
    CHAMADA_PAINEL_TIMERS.MAX_CHAMADAS_PROCESSADAS
  )

  useChamadaAudio(chamadaAtual, isChamando, audioRef)

  useEffect(() => {
    if (chamadasIniciais) {
      setEstadoInicial(chamadasIniciais)
    }
  }, [chamadasIniciais, setEstadoInicial])

  const handleMessage = useCallback(() => {
    const updateWithRetry = async (retriesLeft: number = CHAMADA_PAINEL_TIMERS.MAX_TENTATIVAS_ATMOSPHERE) => {
      try {
        const chamadas = await atualizarChamadas(
          subMinutes(new Date(), CHAMADA_PAINEL_TIMERS.HISTORICO_CHAMADAS_MINUTOS)
        )
        if (chamadas) enfileirar(chamadas)
      } catch (error) {
        if (axios.isCancel(error)) return
        if (retriesLeft > 0) {
          setTimeout(() => updateWithRetry(retriesLeft - 1), CHAMADA_PAINEL_TIMERS.DELAY_RETRY_ATMOSPHERE)
        } else {
          setOnMessageError(true)
        }
      }
    }
    updateWithRetry()
  }, [atualizarChamadas, enfileirar])

  const styles = createStyles(theme, isChamando)

  if (hasError || onMessageError) {
    return <PainelChamadasErrorView title='Ops! Painel de chamadas indisponível no momento' variant='unavailable' />
  }

  const historicoChamadas = chamadasProcessadas.filter((c) => c.id !== chamadaAtual?.id)

  return (
    <>
      {isAuthenticated && <PainelChamadasListener onMessage={handleMessage} />}
      <div css={styles.blueSection}>
        <HeaderPainelChamada nomeUnidadeSaude={nomeUnidadeSaude} />

        <PainelChamadaDestaque chamadaAtual={chamadaAtual} isChamando={isChamando} />
      </div>

      {!isChamando && (
        <VFlow style={styles.whiteSection} vSpacing={1}>
          <Text variant='h1' style={styles.historyTitle}>
            Últimas chamadas
          </Text>

          {historicoChamadas.length > 0 ? (
            <Grid>
              {historicoChamadas.map((chamada) => (
                <Cell size={4} key={chamada.id}>
                  <Card style={styles.historyCard}>
                    <VFlow vSpacing={0.5}>
                      <Text variant='h1'>{chamada.sala}</Text>
                      <Text fontWeight='bold' style={styles.cardNameText}>
                        {chamada.nome.abbreviateMiddleNames()}
                      </Text>
                    </VFlow>
                  </Card>
                </Cell>
              ))}
            </Grid>
          ) : (
            <Text fontSize={1.5} fontStyle='italic'>
              Nenhuma chamada realizada no momento.
            </Text>
          )}
        </VFlow>
      )}
    </>
  )
}

const createStyles = (theme: Theme, isChamando: boolean) => {
  return {
    blueSection: css`
      color: ${theme.pallete.gray.c100};
      background-color: ${theme.pallete.primary.c30};
      flex: ${isChamando ? '1' : '0.6'};
      padding: 2rem 2.5rem 2.625rem;
      display: flex;
      flex-direction: column;
    `,
    whiteSection: css`
      background-color: ${theme.pallete.gray.c90};
      flex: 0.4;
      padding: 2rem 2.5rem 2.4rem;
      display: flex;
      flex-direction: column;
    `,
    historyCard: css`
      min-height: 9.5rem;
    `,
    cardNameText: css`
      font-size: 2rem;
      line-height: 1.2;
    `,
    historyTitle: css`
      color: ${theme.pallete.primary.c30};
    `,
  }
}
